import React from 'react';
import { Text, IconButton } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { Trans } from 'react-i18next';
import { ISelectedCustomerImpact } from './types';
import { formatCurrency, formatNumber } from 'app/utils/currencyUtils';

interface IProps {
    impact: ISelectedCustomerImpact;
    index?: number;
    onRemove: () => void;
}

/**
 * Summary card displaying a selected customer/opportunity with impact data.
 * Matches design: customer name (bold), opportunity ID, impact value, devices count.
 * Delete (trash) icon in top-right corner.
 */
export const CustomerCard: React.FC<IProps> = ({ impact, index, onRemove }) => {
    const summaryId = useId('customer-impact-summary');
    const cardTitle = impact.topic || impact.accountName;
    const displayRevenue = impact.revenueAtRisk !== undefined
        ? formatCurrency(impact.revenueAtRisk)
        : impact.estimatedRevenue !== undefined
            ? formatCurrency(impact.estimatedRevenue)
            : '';

    const displayDevices = impact.devicesAffected !== undefined
        ? formatNumber(impact.devicesAffected)
        : impact.estimatedDevices !== undefined
            ? formatNumber(impact.estimatedDevices)
            : '';
    const accessibleSummary = [
        `${impact.type === 'opportunity' ? 'Opportunity' : 'Customer'} ${cardTitle}`,
        impact.opportunityId ? `Opportunity ID ${impact.opportunityId}` : '',
        displayRevenue ? `Impact ${displayRevenue}` : '',
        displayDevices ? `Devices ${displayDevices}` : '',
    ].filter(Boolean).join('. ');

    return (
        <div className="customer-card" role="listitem">
            <span className="customer-card-screen-reader-summary" id={summaryId}>
                {accessibleSummary}
            </span>
            <div className="card-header">
                <Text className="card-title">{cardTitle}</Text>
                <IconButton
                    className="card-delete-btn"
                    iconProps={{ iconName: 'Delete' }}
                    onClick={onRemove}
                    ariaLabel={`Remove ${cardTitle} from Added Customers and Opportunities`}
                    aria-describedby={summaryId}
                    data-impact-index={index}
                    data-testid="remove-customer-impact"
                />
            </div>

            <div className="card-body">
                {impact.opportunityId && (
                    <div className="card-field">
                        <Text className="card-field-label">
                            <Trans>Opportunity</Trans>:
                        </Text>
                        <Text className="card-field-value">{impact.opportunityId}</Text>
                    </div>
                )}

                {displayRevenue && (
                    <div className="card-field">
                        <Text className="card-field-label">
                            <Trans>Impact</Trans>:
                        </Text>
                        <Text className="card-field-value">{displayRevenue}</Text>
                    </div>
                )}

                {displayDevices && (
                    <div className="card-field">
                        <Text className="card-field-label">
                            <Trans>Devices</Trans>:
                        </Text>
                        <Text className="card-field-value">
                            {displayDevices} <Trans>devices</Trans>
                        </Text>
                    </div>
                )}
            </div>
        </div>
    );
};
